import React, { useEffect, useState } from 'react';
import { Button, Card, Group, Loader, Stack, Switch, Text, Title } from '@mantine/core';
import { modals } from '@mantine/modals';
import useLoadTracker from '@dr-pam/common-components/Hooks/useLoadTracker';
import NotificationUtils from '@dr-pam/common-components/Utils/NotificationUtils';
import type { NdcAwardKind } from '@dr-pam/common-types/database/enums';
import type { NdcAwardConfigurationView } from '@dr-pam/common-components/Models/NdcCertificate';
import { NDC_AWARD_LICENCES, NDC_AWARD_MAINTAINED_BY } from '@dr-pam/common-components/Models/NdcCertificate';
import { getNdcAwardConfiguration, setNdcAwardConfiguration } from '../../services/NdcAwardConfigurationService';
import SearchProgramme from '../programmes/SearchProgramme';
import SearchProduct from '../products/SearchProduct';
import SearchQuiz from '../quizzes/SearchQuiz';
import type { PickedRecord, PickRecordModalProps } from './PickRecordModal';
import PickRecordModal from './PickRecordModal';

const AWARD_NAMES: Record<NdcAwardKind, string> = {
	ACCREDITATION: 'NDC Accreditation',
	FELLOWSHIP: 'NDC Lactation Fellowship',
	PROVIDERS_EDUCATORS: 'NDC for Providers + Educators',
};

type Part = 'programme' | 'examQuiz' | 'maintenanceProduct' | 'maintenanceAgreementQuiz';

const PARTS: { part: Part; label: string; none: string; search: PickRecordModalProps['search'] }[] = [
	{
		part: 'programme',
		label: 'Programme',
		none: 'Not set: no certificate until one is chosen',
		search: (onPicked) => (
			<SearchProgramme
				onSelected={([programme]) => onPicked(programme && { id: programme.id, name: programme.name })}
			/>
		),
	},
	{
		part: 'examQuiz',
		label: 'Exam',
		none: 'None: the certificate is dated the day the programme is completed',
		search: (onPicked) => <SearchQuiz onSelected={(quiz) => onPicked(quiz && { id: quiz.id, name: quiz.title })} />,
	},
	{
		part: 'maintenanceProduct',
		label: 'Maintenance product',
		none: 'None: no Maintenance licence',
		search: (onPicked) => (
			<SearchProduct onSelected={([product]) => onPicked(product && { id: product.id, name: product.name })} />
		),
	},
	{
		part: 'maintenanceAgreementQuiz',
		label: 'Maintenance agreement',
		none: 'None: the licence and certificate download without an agreement',
		search: (onPicked) => <SearchQuiz onSelected={(quiz) => onPicked(quiz && { id: quiz.id, name: quiz.title })} />,
	},
];

/** Which programme, exam, Maintenance product and agreement belong to each NDC award. */
export default function NdcAwardConfigurations() {
	const [saved, setSaved] = useState<NdcAwardConfigurationView[]>([]);
	const [drafts, setDrafts] = useState<NdcAwardConfigurationView[]>([]);
	const { addLoader, removeLoader, isLoading } = useLoadTracker();

	const show = (awards: NdcAwardConfigurationView[]) => {
		setSaved(awards);
		setDrafts(awards);
	};

	useEffect(() => {
		const loader = addLoader();
		const request = getNdcAwardConfiguration();
		let active = true;
		request.response
			.then((awards) => active && show(awards))
			.catch((err) => active && NotificationUtils.showError(err as Error, 'Failed to load the NDC awards.'))
			.finally(() => removeLoader(loader));
		return () => {
			active = false;
			request.abort();
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// An exam belongs to its programme and an agreement to its Maintenance product: a new one clears it.
	const change = (kind: NdcAwardKind, part: Part, record: PickedRecord | null) =>
		setDrafts((current) =>
			current.map((award) => {
				if (award.kind !== kind) {
					return award;
				}
				const changed = { ...award, [part]: record };
				if (part === 'programme' && record?.id !== award.programme?.id) {
					changed.examQuiz = null;
				}
				if (part === 'maintenanceProduct' && record?.id !== award.maintenanceProduct?.id) {
					changed.maintenanceAgreementQuiz = null;
				}
				return changed;
			}),
		);

	const toggleCertificate = (kind: NdcAwardKind, isCertificateEnabled: boolean) =>
		setDrafts((current) =>
			current.map((award) => (award.kind === kind ? { ...award, isCertificateEnabled } : award)),
		);

	const pick = (kind: NdcAwardKind, part: Part, label: string, search: PickRecordModalProps['search']) => {
		const modalId = `ndc-award-${kind}-${part}`;
		modals.open({
			modalId,
			title: `${AWARD_NAMES[kind]}: ${label}`,
			children: (
				<PickRecordModal modalId={modalId} search={search} onPicked={(record) => change(kind, part, record)} />
			),
		});
	};

	const save = async (draft: NdcAwardConfigurationView) => {
		if (!draft.programme) {
			return;
		}
		const loader = addLoader();
		// An award with no licence design has no Maintenance, whatever was stored.
		const hasLicence = Boolean(NDC_AWARD_LICENCES[draft.kind]);
		try {
			const awards = await setNdcAwardConfiguration(draft.kind, {
				programmeId: draft.programme.id,
				examQuizId: draft.examQuiz?.id ?? null,
				maintenanceProductId: (hasLicence && draft.maintenanceProduct?.id) || null,
				maintenanceAgreementQuizId: (hasLicence && draft.maintenanceAgreementQuiz?.id) || null,
				isCertificateEnabled: draft.isCertificateEnabled,
			}).response;
			// Only this award's edits are saved; another award's unsaved edits stay.
			setSaved(awards);
			setDrafts((current) =>
				current.map((award) =>
					award.kind === draft.kind ? (awards.find(({ kind }) => kind === draft.kind) ?? award) : award,
				),
			);
			NotificationUtils.showSuccess(`Saved ${AWARD_NAMES[draft.kind]}`, 'Success');
		} catch (err) {
			NotificationUtils.showError(err as Error, `Failed to save ${AWARD_NAMES[draft.kind]}`);
		} finally {
			removeLoader(loader);
		}
	};

	return (
		<Stack maw={720}>
			<Text size="sm" c="dimmed">
				What earns, dates and maintains each award. A programme or Maintenance product cannot be swapped while
				members hold the award through it, so no one&apos;s certificate or licence disappears. A new agreement
				asks every member in Maintenance to pass it before their licence and certificate download again.
			</Text>
			{isLoading && drafts.length === 0 && <Loader />}
			{drafts.map((draft) => {
				const isChanged =
					JSON.stringify(draft) !== JSON.stringify(saved.find(({ kind }) => kind === draft.kind));
				const parts = PARTS.filter(
					({ part }) =>
						NDC_AWARD_LICENCES[draft.kind] ||
						(part !== 'maintenanceProduct' && part !== 'maintenanceAgreementQuiz'),
				);
				const maintainedBy = NDC_AWARD_MAINTAINED_BY[draft.kind];
				return (
					<Card key={draft.kind} withBorder>
						<Stack gap="sm">
							<Title order={3}>{AWARD_NAMES[draft.kind]}</Title>
							{draft.problems.map((problem) => (
								<Text key={problem} size="sm" c="red">
									{problem}
								</Text>
							))}
							{maintainedBy !== draft.kind && (
								<Text size="sm" c="dimmed">
									Maintained through {AWARD_NAMES[maintainedBy]}: its Maintenance agreement, when one
									is set, locks this certificate too.
								</Text>
							)}
							<Switch
								checked={draft.isCertificateEnabled}
								onChange={(event) => toggleCertificate(draft.kind, event.currentTarget.checked)}
								label="Certificate available"
								description="Off hides the certificate from every member who earned it, while it is being finalised"
								disabled={isLoading || !draft.programme}
							/>
							{parts.map(({ part, label, none, search }) => (
								<Group key={part} justify="space-between" wrap="nowrap">
									<Stack gap={0}>
										<Text size="sm" fw={600}>
											{label}
										</Text>
										<Text size="sm" c={draft[part] ? undefined : 'dimmed'}>
											{draft[part]?.name ?? none}
										</Text>
									</Stack>
									<Group gap="xs" wrap="nowrap">
										<Button
											variant="subtle"
											size="xs"
											disabled={isLoading}
											onClick={() => pick(draft.kind, part, label, search)}
											aria-label={`Change ${label} of ${AWARD_NAMES[draft.kind]}`}
										>
											Change
										</Button>
										{part !== 'programme' && draft[part] && (
											<Button
												variant="subtle"
												size="xs"
												color="red"
												disabled={isLoading}
												onClick={() => change(draft.kind, part, null)}
												aria-label={`Clear ${label} of ${AWARD_NAMES[draft.kind]}`}
											>
												Clear
											</Button>
										)}
									</Group>
								</Group>
							))}
							<Group justify="flex-end">
								<Button
									disabled={isLoading || !isChanged || !draft.programme}
									onClick={() => save(draft)}
								>
									Save {AWARD_NAMES[draft.kind]}
								</Button>
							</Group>
						</Stack>
					</Card>
				);
			})}
		</Stack>
	);
}
